import { useRef, type ReactNode } from "react";
import { ArrowRight, Rocket, Sparkles } from "lucide-react";
import { getHostLabel, getLaunchCommand } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useChatStore } from "@/stores/chat-store";
import type { HelpStepComponentProps } from "../help-flows";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives  (same pattern as cli-setup-steps)                      */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
          {step}
        </div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
        {step}
      </div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Exported step component                                                   */
/* -------------------------------------------------------------------------- */

export function VerificationStep({ onAdvance }: Pick<HelpStepComponentProps, "onAdvance">) {
  const host = useResolvedHost();
  const openHelpCenter = useChatStore((store) => store.openHelpCenter);

  const handleStartEpicRequest = () => {
    openHelpCenter("wizard", "create-request");
  };

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title={`Confirm ${getHostLabel(host)} is ready`}>
        <p className="text-sm leading-7 text-muted-foreground">
          Open {getHostLabel(host)} and make sure you see a success message like this before moving on.
        </p>

        <CopyableTerminal title="What you should see">
          <div className="space-y-1.5">
            <p>
              <span className="text-foreground/45">$ </span>
              {getLaunchCommand(host)}
            </p>
            <p className="text-muted-foreground/70">Starting {getHostLabel(host)}…</p>
            <p className="text-emerald-400">✓ Connected. All tools enabled.</p>
          </div>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={2} title={"What's next"}>
        <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] to-primary/[0.03] p-6">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/15 text-primary">
              <Rocket className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {"What's next"}
              </p>
              <h3 className="mt-2 text-xl font-semibold text-foreground">
                Time to create your first epic request
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                An Epic Request is the simple form you use to suggest a new idea or feature. The Request Formulator walks you through it — just describe what you want to build.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleStartEpicRequest}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <Sparkles className="h-4 w-4" />
                  Create an Epic Request
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { onAdvance?.(); }}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.08]"
                >
                  Back to Help Center
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick reference */}
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
            Quick reference
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">
                {host === "copilot" ? "@request-formulator" : "/spectree:request-formulator"}
              </p>
              <p className="text-xs leading-5 text-muted-foreground">Start a new idea request</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">
                {host === "copilot" ? "@planner" : "@agent-spectree:planner"}
              </p>
              <p className="text-xs leading-5 text-muted-foreground">Turn a request into a clear plan</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">
                {/* Claude: the orchestrator agent stalls on a whole epic; the workflow is the epic path. */}
                {host === "copilot" ? "@orchestrator" : "/spectree:spectree-epic-execute"}
              </p>
              <p className="text-xs leading-5 text-muted-foreground">Run the plan with SpecTree agents</p>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}
